import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import SelectDepartmentModal from 'components/SelectDepartmentModal';
import AlertModal from 'components/common/AlertModal';
import DeliveryProgressModal from 'components/common/DeliveryProgressModal';
import ErrorDataSummaryModal from 'components/common/ErrorDataSummaryModal/ErrorDataSummaryModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import deliveryService from 'features/Delivery/deliveryService';
import {
  deliveryListActions,
  selectDeliveryCounterPattern,
  selectDeliverySaleType,
} from 'features/Delivery/deliverySlice';
import { DepartmentNode } from 'features/common/models';
import { workerActions } from 'features/common/workerSlice';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Col, Form, Modal, ModalProps, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { FEATURE_DELIVERY_ID, displayError, handleServiceError } from 'utils';
import {
  STATUS_RESPONSE_COMPLETED,
  STATUS_RESPONSE_FAILED,
  STATUS_RESPONSE_SUCCESS,
  TARGET_SCHEMA_DEFAULT,
  UNIT,
  UNIT_COUNTER_PATTERN,
  UNIT_SALE_TYPE,
  UNIT_STORE_SPECIFIC,
} from '../../constants';
import { DeliverySetting, TargetSchema } from '../../models/delivery';
import CustomInputDatePicker from '../CustomInputDatePicker';

const DeliverySettingsModal = forwardRef<ModalProps, { onCreateSuccess: () => void }>((props, ref) => {
  const { t } = useTranslation();
  const { showGlobalModal, closeGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // selector
  const saleTypes = useAppSelector(selectDeliverySaleType);
  const counterPatterns = useAppSelector(selectDeliveryCounterPattern);

  // state
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [dataDate, setDataDate] = useState<Date | null>(new Date());
  const [selectedUnit, setSelectedUnit] = useState<number>(UNIT_SALE_TYPE); // State for selected distribution unit
  const [showSelectDepartmentModal, setShowSelectDepartmentModal] = useState(false);
  const [content, setContent] = useState('');
  const [counterPattern, setCounterPattern] = useState(counterPatterns[0].id || 1); // Default value
  const [saleType, setSaleType] = useState(saleTypes[0].id || 1); // Default value
  const [targetRestaurantId, setTargetRestaurantId] = useState<number[]>([]); // state for storing a list of target restaraurantid
  const [selectedTargetSchema, setSelectedTargetSchema] = useState(TARGET_SCHEMA_DEFAULT.target_schema);
  const [targetSchemaList, setTargetSchemaList] = useState<TargetSchema[]>([TARGET_SCHEMA_DEFAULT]);
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleDataDateChange = (date: Date | null) => {
    setDataDate(date);
  };

  // Add this function to handle changes to the Distribution Unit select input
  const handleUnitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUnit(Number(event.target.value));
    if (Number(event.target.value) === UNIT_STORE_SPECIFIC) {
      setShowSelectDepartmentModal(true);
    } else {
      setShowSelectDepartmentModal(false);
    }
  };

  useImperativeHandle(ref, () => ({
    closeModal() {
      setShowModal(false);
    },
    openModal() {
      setShowModal(true);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow);
      setDataDate(tomorrow);
      setSelectedUnit(UNIT_COUNTER_PATTERN); // Set to UNIT_SALE_TYPE whenever modal is opened
      setShowSelectDepartmentModal(false); // reset state of show SelectDepartment component
      setContent('');
      setCounterPattern(counterPatterns[0].id || 1); // reset state counterPattern
      setSaleType(saleTypes[0].id || 1); // reset state saleType
      setTargetRestaurantId([]); // clear state targetRestaurantId
      fetchTargetSchemaList();
    },
  }));

  function handleReturnSelectedDepartment(departments: DepartmentNode[]) {
    const restaurantIds = departments
      .filter((department) => department.restaurant_id !== null)
      .map((department) => department.restaurant_id) as number[];
    setTargetRestaurantId(restaurantIds);
  }

  const processCreateDeliverySettings = (data: DeliverySetting) => {
    deliveryService
        .createDeliveryAsync(data)
        .then((response) => {
          if (response.status === STATUS_RESPONSE_SUCCESS) {
            // Save task info to Redux store
            dispatch(
              deliveryListActions.setAsyncCreationTaskStarted({
                taskId: response.data.task_id,
                progress: response.data.progress || 'Initializing...',
              })
            );

            // Start worker state and polling
            dispatch(workerActions.setWorkerSuccess(response));

            // Show alert that delivery creation is in progress
            showGlobalModal(() => (
              <DeliveryProgressModal
                title={t('App.info')}
                message={
                  t('DeliveryFeature.Text.delivery_creation_in_progress') ||
                  'Delivery creation is in progress. Please wait...'
                }
                isProcessing={true}
              />
            ));

            // Start polling for task status
            startPolling(response.data.task_id);

            setShowModal(false);
          } else {
            // Handle error
            showGlobalModal(() => <AlertModal title={t('App.error')} message={response.message} />);
          }
        })
        .catch((e) => {
          // Handle error here
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        });
  } // Ref to prevent multiple submissions

  const handleCreateDeliverySettings = (skips_check_errors: boolean = false) => {
    const delivery_date_formatted = selectedDate ? selectedDate.toISOString().slice(0, 10) : '';
    const data_date_formatted = dataDate ? dataDate.toISOString().slice(0, 10) : '';

    let data: DeliverySetting = {
      delivery_date: delivery_date_formatted,
      data_date: data_date_formatted,
      content: content,
      unit: selectedUnit,
      skips_check_errors: skips_check_errors,
      target_schema: selectedTargetSchema,
    };

    if (selectedUnit === UNIT_SALE_TYPE) {
      data.sale_type = saleType;
    } else if (selectedUnit === UNIT_COUNTER_PATTERN) {
      data.counter_pattern = counterPattern;
    } else if (selectedUnit === UNIT_STORE_SPECIFIC) {
      data.restaurant_ids = targetRestaurantId;
    }

    if (skips_check_errors === false) {
      // First check for errors
      deliveryService
        .deliveryCheckError(data)
        .then((response) => {
          if (
            Array.isArray(response.data.restaurants_checked_result) &&
            response.data.restaurants_checked_result.length > 0
          ) {
            showGlobalModal(() => (
              <ErrorDataSummaryModal
                errorFromFeatureId={FEATURE_DELIVERY_ID}
                data={response.data.restaurants_checked_result}
                onDeliverySubmit={handleContinueDeliverySubmit}
              />
            ));
          } else {
            // Use async API for delivery creation
            processCreateDeliverySettings(data);
          }
        })
        .catch((e) => {
          // Handle error here
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        });
    } else {
      // Use async API for delivery creation
      processCreateDeliverySettings(data);
    }
  };
  const handleContinueDeliverySubmit = () => {
    handleCreateDeliverySettings(true);
  };
  const pollingInterval = parseInt(process.env.REACT_APP_WORKER_STATUS_POLLING_INTERVAL || '30000', 10);
  // Start polling interval
  const startPolling = (taskId: string) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      checkDeliveryCreationStatus(taskId);
    }, pollingInterval); // Poll every 3 seconds
  };

  // Function to check delivery creation status
  const checkDeliveryCreationStatus = useCallback(
    async (taskId: string) => {
      try {
        const response = await deliveryService.getAsyncDeliveryCreateStatus(taskId);
        if (response.status === STATUS_RESPONSE_SUCCESS && response.data) {
          const { status } = response.data;

          // Update worker state
          dispatch(workerActions.setWorkerSuccess(response));

          // Update delivery state with progress
          if (status === 'progressing') {
            dispatch(
              deliveryListActions.setAsyncCreationTaskProgressing({
                progress: response.data.progress || 'Processing...',
              })
            );
          }

          // Check if task is completed or failed
          if (status === STATUS_RESPONSE_FAILED || status === STATUS_RESPONSE_COMPLETED) {
            // Clear the interval
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }

            // Reset worker state
            dispatch(workerActions.resetWorker());

            if (status === STATUS_RESPONSE_COMPLETED) {
              // Update delivery state - DON'T show toast immediately, will show on next page load
              dispatch(deliveryListActions.setAsyncCreationTaskCompleted({ showToast: false }));

              // Show success modal
              showGlobalModal(() => (
                <AlertModal
                  title={t('App.info')}
                  message={t('DeliveryFeature.Text.delivery_created_successfully') || 'Delivery created successfully!'}
                  onClose={() => {
                    closeGlobalModal();
                  }}
                />
              ));

              // Reload delivery list
              props.onCreateSuccess();

              // Reset task after showing modal
              setTimeout(() => {
                dispatch(deliveryListActions.resetAsyncCreationTask());
              }, 1000);
            } else if (status === STATUS_RESPONSE_FAILED) {
              const msgError = response.data.error || t('App.error');

              // Update delivery state - DON'T show toast immediately, will show on next page load
              dispatch(
                deliveryListActions.setAsyncCreationTaskFailed({
                  error: msgError || 'Unknown error',
                  showToast: false,
                })
              );

              // Show error modal
              showGlobalModal(() => (
                <AlertModal
                  title={t('App.error')}
                  message={msgError ?? t('App.error')}
                  onClose={() => {
                    closeGlobalModal();
                  }}
                />
              ));

              // Reset task after showing modal
              setTimeout(() => {
                dispatch(deliveryListActions.resetAsyncCreationTask());
              }, 1000);
            }
          }
          // For other statuses (like 'started' or 'progressing'), continue polling
        }
      } catch (error) {
        console.error('Failed to check delivery creation status:', error);
        dispatch(workerActions.setWorkerFailed());

        // Clear the interval on error
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    },
    [dispatch, showGlobalModal, t, props, closeGlobalModal]
  );

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Add event handlers to update state when the input/select values change
  const handleContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(event.target.value);
  };

  const handleCounterPatternChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCounterPattern(Number(event.target.value));
  };

  const handleSaleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSaleType(Number(event.target.value));
  };
  const fetchTargetSchemaList = async () => {
    try {
      const response = await deliveryService.getTargetSchemaList();
      setTargetSchemaList([TARGET_SCHEMA_DEFAULT, ...response.data]);
    } catch (e: AxiosError | any) {
      console.error('Error fetching target schema list:', e.message);
      const data: any = e.response?.data;
      showGlobalModal(() => <AlertModal title={t('App.info')} message={e.message || data?.message} />);
    }
  };

  const handleSelectedTargetSchema = (event: any) => {
    const selectedOptionValue = event.target.value;
    setSelectedTargetSchema(selectedOptionValue);
  };

  return (
    <Modal aria-labelledby="contained-modal-title-vcenter" centered show={showModal}>
      <Modal.Body>
        <Row className="mb-3">
          <Col sm={9}>
            <div className="p-2 h5" style={{ backgroundColor: '#E1D5E7' }}>
              <strong>{t('DeliveryFeature.Text.distribution_settings')}</strong>
            </div>
          </Col>
          <Col></Col>
        </Row>
        <Row className="mb-3">
          <Form.Label column sm="3">
            {t('DeliveryFeature.Text.target_schema_default')}
          </Form.Label>
          <Col sm="9">
            <div className="d-flex" style={{ minWidth: '180px' }}>
              <Form.Select onChange={handleSelectedTargetSchema} value={selectedTargetSchema}>
                {targetSchemaList.map((option) => (
                  <option key={option.target_schema} value={option.target_schema}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Form.Label column sm="3">
            {t('DeliveryFeature.Text.delivery_date')}:
          </Form.Label>
          <Col sm="9">
            <div className="d-flex" style={{ maxWidth: '150px' }}>
              <DatePicker
                locale="ja"
                selected={selectedDate}
                onChange={handleDateChange}
                customInput={<CustomInputDatePicker />}
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Form.Label column sm="3">
            {t('DeliveryFeature.Text.data_date')}:
          </Form.Label>
          <Col sm="9">
            <div className="d-flex" style={{ maxWidth: '150px' }}>
              <DatePicker
                locale="ja"
                selected={dataDate}
                onChange={handleDataDateChange}
                customInput={<CustomInputDatePicker />}
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Form.Label column sm="3">
            {t('DeliveryFeature.Text.content')}:
          </Form.Label>
          <Col sm="9">
            <div className="d-flex" style={{ minWidth: '180px' }}>
              <textarea className="form-control" id="content" rows={3} onChange={handleContentChange} />
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Form.Label column sm="3">
            {t('DeliveryFeature.Text.distribution_unit')}:
          </Form.Label>
          <Col sm="9">
            <div className="d-flex" style={{ minWidth: '180px' }}>
              <Form.Select onChange={handleUnitChange}>
                {UNIT.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {showSelectDepartmentModal && (
              <div className="my-3">
                <SelectDepartmentModal
                  defaultDataDate={dataDate || new Date()}
                  showDataDatePicker={false}
                  onClose={() => setShowSelectDepartmentModal(false)}
                  onReflect={handleReturnSelectedDepartment}
                />
              </div>
            )}
          </Col>
        </Row>

        {/* Conditionally render the Sales Type dropdown */}
        {selectedUnit === UNIT_SALE_TYPE && (
          <Row className="mb-3">
            <Form.Label column sm="3">
              {t('DeliveryFeature.Text.sales_type')}:
            </Form.Label>
            <Col sm="9">
              <div className="d-flex" style={{ minWidth: '180px' }}>
                <Form.Select onChange={handleSaleTypeChange}>
                  {saleTypes.map((saleType) => (
                    <option key={saleType.id} value={saleType.id}>
                      {saleType.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
          </Row>
        )}
        {/* Conditionally render the Counter Pattern dropdown */}
        {selectedUnit === UNIT_COUNTER_PATTERN && (
          <Row className="mb-3">
            <Form.Label column sm="3">
              {t('DeliveryFeature.Text.counter_pattern')}:
            </Form.Label>
            <Col sm="9">
              <div className="d-flex" style={{ minWidth: '180px' }}>
                <Form.Select onChange={handleCounterPatternChange}>
                  {counterPatterns.map((counterPattern) => (
                    <option key={counterPattern.id} value={counterPattern.id}>
                      {counterPattern.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
              <button
                className="btn btn-primary me-md-2"
                type="button"
                onClick={handleCreateDeliverySettings.bind(null, false)}
              >
                {t('DeliveryFeature.Component.setting')}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                {t('DeliveryFeature.Component.close')}
              </button>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
});

export default DeliverySettingsModal;
